import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RorosAgentService,
  RorosAgentMemoryService,
  RorosTaskDelegationService,
} from '@/services/RorosService';
import { rorosKeys } from './keys';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Agent Definitions — Queries ───────────────────────────────────────────

export function useRorosAgents() {
  return useQuery({
    queryKey: rorosKeys.agents.all,
    queryFn: () => RorosAgentService.list(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosAgent(id: string) {
  return useQuery({
    queryKey: rorosKeys.agents.detail(id),
    queryFn: () => RorosAgentService.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Agent Definitions — Mutations ─────────────────────────────────────────

export function useRorosAgentCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosAgentService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.agents.all });
    },
  });
}

export function useRorosAgentClone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosAgentService.clone(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.agents.all });
    },
  });
}

export function useRorosAgentExecute() {
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosAgentService.execute(payload),
  });
}

export function useRorosAgentUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosAgentService.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.agents.all });
      qc.invalidateQueries({ queryKey: rorosKeys.agents.detail(id) });
    },
  });
}

export function useRorosAgentRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosAgentService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.agents.all });
    },
  });
}

// ─── Agent Memory — Queries ────────────────────────────────────────────────

export function useRorosAgentMemories(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.agentMemory.all, params] as const,
    queryFn: () => RorosAgentMemoryService.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosAgentMemory(id: string) {
  return useQuery({
    queryKey: rorosKeys.agentMemory.detail(id),
    queryFn: () => RorosAgentMemoryService.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Agent Memory — Mutations ──────────────────────────────────────────────

export function useRorosAgentMemoryCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosAgentMemoryService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.agentMemory.all });
    },
  });
}

export function useRorosAgentMemoryUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosAgentMemoryService.update(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.agentMemory.all });
      qc.invalidateQueries({ queryKey: rorosKeys.agentMemory.detail(id) });
    },
  });
}

export function useRorosAgentMemoryRemove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosAgentMemoryService.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.agentMemory.all });
    },
  });
}

// ─── Task Delegations — Queries ────────────────────────────────────────────

export function useRorosTaskDelegations(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.taskDelegations.all, params] as const,
    queryFn: () => RorosTaskDelegationService.list(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosTaskDelegation(id: string) {
  return useQuery({
    queryKey: rorosKeys.taskDelegations.detail(id),
    queryFn: () => RorosTaskDelegationService.get(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Task Delegations — Mutations ──────────────────────────────────────────

export function useRorosTaskDelegationCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosTaskDelegationService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.taskDelegations.all });
    },
  });
}

export function useRorosTaskDelegationUpdateStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosTaskDelegationService.updateStatus(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.taskDelegations.all });
      qc.invalidateQueries({ queryKey: rorosKeys.taskDelegations.detail(id) });
    },
  });
}

export function useRorosTaskDelegationCancel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => RorosTaskDelegationService.cancel(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: rorosKeys.taskDelegations.all });
      qc.invalidateQueries({ queryKey: rorosKeys.taskDelegations.detail(id) });
    },
  });
}
